import React, { useState } from 'react';
import { Sparkles, User, Wrench, ChevronDown, ChevronRight } from 'lucide-react';
import type { WorkbenchMessage, MessageContentBlock, ToolUseBlock, ToolResultBlock } from '../../../types';
// Rich Reltio-style blocks (reused — the standalone page was retired but
// these presentation components live on and render inside this platform UI).
import { SchemaProposalTable } from './blocks/components/tables/SchemaProposalTable';
import { ColumnQualityTable } from './blocks/components/tables/ColumnQualityTable';
import { FindingsCallout, FinalSummaryCallout } from './blocks/components/callouts/Callouts';
import { PriorityBucket } from './blocks/components/cleanup/CleanupCards';
import { QualitySummaryBlock } from './blocks/components/blocks/AgentBlocks';

// Tools that have a rich detail card replacing their raw JSON tool_result
const DETAIL_CARD_TOOLS = new Set([
  'read_table_for_profiling',
  'propose_schema_labels',
  'run_quality_assessment',
]);

interface Props {
  message: WorkbenchMessage;
  artifacts?: Record<string, any>;
  // IDs of tool_use blocks that have a detail card rendered — their raw
  // tool_result should be suppressed to avoid duplicate/redundant display.
  detailCardToolUseIds?: Set<string>;
}

const MessageBubble: React.FC<Props> = ({ message, artifacts, detailCardToolUseIds }) => {
  const isUser = message.role === 'user';
  const content = message.content;

  // Tool-result wrapper messages (role=user, content=[tool_result,...]) are noise
  // for the user — show a compact indicator instead of a full bubble.
  if (
    !isUser
      ? false
      : Array.isArray(content) && content.every((b: any) => b?.type === 'tool_result')
  ) {
    // Hide tool_results whose matching tool_use already has a detail card
    const visible = (content as ToolResultBlock[]).filter(
      (b) => !detailCardToolUseIds?.has(b.tool_use_id),
    );
    if (visible.length === 0) return null;
    return <ToolResultsStrip blocks={visible} />;
  }

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
          isUser ? 'bg-cargo-accent/20' : 'bg-cargo-accent text-white'
        }`}
      >
        {isUser ? <User className="w-4 h-4 text-cargo-accent" /> : <Sparkles className="w-4 h-4" />}
      </div>

      <div className={`flex-1 max-w-[80%] ${isUser ? 'text-right' : ''}`}>
        <div className="text-cargo-muted text-[11px] uppercase tracking-wider mb-1">
          {isUser ? 'You' : 'Profiling Agent'}
          {message.at && (
            <span className="ml-2 normal-case tracking-normal opacity-70">
              {new Date(message.at).toLocaleTimeString()}
            </span>
          )}
        </div>

        {/* Content */}
        {typeof content === 'string' ? (
          <div
            className={`inline-block text-left px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
              isUser
                ? 'bg-cargo-accent text-white'
                : 'bg-cargo-panel border border-cargo-border text-cargo-text'
            }`}
          >
            {content}
          </div>
        ) : (
          <div className="space-y-2">
            {(content as MessageContentBlock[]).map((block, i) => (
              // Wrap each block in a div so inline-block bubbles don't float
              // next to each other on the same line — each block stacks below
              // the previous one in arrival order.
              <div key={i}>
                <Block block={block} isUser={isUser} />
                {/* Inline detail card after a known tool call */}
                {block.type === 'tool_use' && (
                  <DetailAfterToolUse block={block as ToolUseBlock} artifacts={artifacts} />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Block: React.FC<{ block: MessageContentBlock; isUser: boolean }> = ({ block, isUser }) => {
  if (block.type === 'text') {
    return (
      <div
        className={`inline-block text-left px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
          isUser
            ? 'bg-cargo-accent text-white'
            : 'bg-cargo-panel border border-cargo-border text-cargo-text'
        }`}
      >
        {renderMarkdown(block.text || '', isUser)}
      </div>
    );
  }
  if (block.type === 'tool_use') {
    return <ToolUseCard block={block} />;
  }
  if (block.type === 'tool_result') {
    return <ToolResultCard block={block} />;
  }
  return null;
};

const ToolUseCard: React.FC<{ block: ToolUseBlock }> = ({ block }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="inline-block text-left bg-cargo-surface border border-cargo-border/60 rounded-lg px-3 py-2 text-xs">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 text-cargo-muted hover:text-cargo-text"
      >
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        <Wrench className="w-3 h-3 text-cargo-accent" />
        <span className="font-mono">{block.name}</span>
        <span className="text-cargo-muted opacity-60">called</span>
      </button>
      {open && (
        <pre className="mt-2 bg-cargo-surface/80 border border-cargo-border/40 rounded p-2 text-[10px] text-cargo-muted overflow-auto max-h-40">
          {JSON.stringify(block.input, null, 2)}
        </pre>
      )}
    </div>
  );
};

const ToolResultCard: React.FC<{ block: ToolResultBlock }> = ({ block }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="inline-block text-left bg-cargo-surface border border-cargo-border/60 rounded-lg px-3 py-2 text-xs">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 text-cargo-muted hover:text-cargo-text"
      >
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        <Wrench className="w-3 h-3 text-cargo-success" />
        <span className="text-cargo-muted opacity-80">tool result</span>
      </button>
      {open && (
        <pre className="mt-2 bg-cargo-surface/80 border border-cargo-border/40 rounded p-2 text-[10px] text-cargo-muted overflow-auto max-h-60">
          {safePretty(block.content)}
        </pre>
      )}
    </div>
  );
};

const ToolResultsStrip: React.FC<{ blocks: ToolResultBlock[] }> = ({ blocks }) => (
  <div className="flex gap-3 justify-start">
    <div className="w-8 flex-shrink-0" />
    <div className="flex gap-1 flex-wrap">
      {blocks.map((b, i) => (
        <ToolResultCard key={i} block={b} />
      ))}
    </div>
  </div>
);

function safePretty(s: string): string {
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}

/**
 * Lightweight, dependency-free markdown renderer for agent chat text.
 *
 * The agent emits markdown — section headings (`## Summary`), bold
 * labels (`**Focus**:`), bullet lists (`• item` / `- item`), inline
 * code (`` `col` ``) and links. Rendering it raw shows literal `##`
 * and `**` in the bubble. This parses the common subset properly so
 * the bubble looks clean. No external library (keeps the bundle lean).
 *
 *   renderInline  — bold / code / links WITHIN one line
 *   renderMarkdown — line structure: headings, bullets, paragraphs
 */
function renderInline(text: string, isUser: boolean, keyBase: string): React.ReactNode[] {
  const linkClass = isUser
    ? 'underline decoration-white/60 hover:decoration-white'
    : 'text-cargo-accent hover:underline';
  const codeClass = isUser
    ? 'px-1 py-0.5 rounded bg-white/20 font-mono text-[12px]'
    : 'px-1 py-0.5 rounded bg-cargo-hover text-cargo-accent font-mono text-[12px]';

  // Tokenise on **bold**, `code`, [label](url), and bare URLs/paths.
  const TOKEN = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\)|https?:\/\/[^\s)]+|\/[\w-]+\/[\w/\-?&=.#%]+)/g;
  const out: React.ReactNode[] = [];
  let last = 0;
  let k = 0;
  let m: RegExpExecArray | null;
  while ((m = TOKEN.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('**') && tok.endsWith('**')) {
      out.push(<strong key={`${keyBase}-b${k++}`}>{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith('`') && tok.endsWith('`')) {
      out.push(<code key={`${keyBase}-c${k++}`} className={codeClass}>{tok.slice(1, -1)}</code>);
    } else if (tok.startsWith('[')) {
      const mm = /\[([^\]]+)\]\(([^)]+)\)/.exec(tok);
      if (mm) {
        const ext = /^https?:/i.test(mm[2]);
        out.push(
          <a key={`${keyBase}-l${k++}`} href={mm[2]}
             target={ext ? '_blank' : undefined}
             rel={ext ? 'noopener noreferrer' : undefined}
             className={linkClass}>{mm[1]}</a>,
        );
      } else {
        out.push(tok);
      }
    } else {
      const ext = /^https?:/i.test(tok);
      out.push(
        <a key={`${keyBase}-u${k++}`} href={tok}
           target={ext ? '_blank' : undefined}
           rel={ext ? 'noopener noreferrer' : undefined}
           className={linkClass}>{tok}</a>,
      );
    }
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out.length ? out : [text];
}

function renderMarkdown(text: string, isUser: boolean): React.ReactNode {
  const lines = text.split('\n');
  const out: React.ReactNode[] = [];
  let bullets: React.ReactNode[] = [];
  let bi = 0;

  const flush = () => {
    if (bullets.length) {
      out.push(
        <ul key={`ul-${out.length}`} className="list-disc ml-5 my-1 space-y-0.5">
          {bullets}
        </ul>,
      );
      bullets = [];
    }
  };

  lines.forEach((line, i) => {
    const t = line.trim();
    const heading = /^#{1,4}\s+(.*)$/.exec(t);
    const bullet = /^[•\-*]\s+(.*)$/.exec(t);
    if (heading) {
      flush();
      out.push(
        <div key={`h-${i}`} className="font-semibold text-[14px] mt-2 mb-1 first:mt-0">
          {renderInline(heading[1], isUser, `h${i}`)}
        </div>,
      );
    } else if (bullet) {
      bullets.push(
        <li key={`li-${i}-${bi++}`}>{renderInline(bullet[1], isUser, `b${i}`)}</li>,
      );
    } else if (t === '') {
      flush();
      // blank line → small vertical gap (skip leading/trailing)
      if (i > 0 && i < lines.length - 1) {
        out.push(<div key={`sp-${i}`} className="h-1.5" />);
      }
    } else {
      flush();
      out.push(
        <div key={`p-${i}`} className="my-0.5">
          {renderInline(line, isUser, `p${i}`)}
        </div>,
      );
    }
  });
  flush();
  return out;
}

/**
 * Renders the rich Reltio-style blocks next to a tool_use block.
 * Reads from session artifacts (passed from ConversationPage):
 *
 *   read_table_for_profiling / propose_schema_labels → artifacts.preview
 *       → Schema Proposal table + Data Quality Observations callout
 *
 *   run_quality_assessment → artifacts.workspace_result
 *       → Quality Summary + Column Quality table + Cleanup buckets
 *         + Final Summary callout
 */
const DetailAfterToolUse: React.FC<{
  block: ToolUseBlock;
  artifacts?: Record<string, any>;
}> = ({ block, artifacts }) => {
  if (!artifacts) return null;
  const toolName = block.name;

  // ── Schema proposal (Stage 3) ─────────────────────────────────────
  if (toolName === 'propose_schema_labels' && artifacts.preview) {
    const preview = artifacts.preview;
    if (preview._awaiting_llm_labels) return null;
    return (
      <div className="mt-3 space-y-4">
        {Array.isArray(preview.columns) && preview.columns.length > 0 && (
          <SchemaProposalTable columns={preview.columns} />
        )}
        {Array.isArray(preview.observations) && preview.observations.length > 0 && (
          <FindingsCallout observations={preview.observations} />
        )}
      </div>
    );
  }

  // read_table_for_profiling shows a compact note; the rich table
  // appears under propose_schema_labels (after the LLM reasons).
  if (toolName === 'read_table_for_profiling' && artifacts.preview) {
    const p = artifacts.preview;
    if (p._awaiting_llm_labels) {
      return (
        <div className="mt-2 text-[12px] text-cargo-muted">
          Sampled {p.sample_size} rows from ~{(p.row_count_estimate ?? 0).toLocaleString()} total
          {' '}— {p.column_count} columns ready for analysis.
        </div>
      );
    }
    return null;
  }

  // ── Full assessment result (Stage 4-8) ────────────────────────────
  if (toolName === 'run_quality_assessment' && artifacts.workspace_result) {
    const r = artifacts.workspace_result;
    return (
      <div className="mt-3 space-y-6">
        {r.quality_summary && (
          <QualitySummaryBlock
            overallScore={r.quality_summary.overall_score}
            recordCount={r.quality_summary.record_count}
            narrative={r.quality_summary.narrative}
            distribution={r.quality_summary.distribution}
            topPriority={r.quality_summary.top_priority}
          />
        )}
        {Array.isArray(r.column_quality_rows) && r.column_quality_rows.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-cargo-text font-semibold text-[15px]">
              Column Quality Analysis
            </h3>
            <ColumnQualityTable rows={r.column_quality_rows} />
          </div>
        )}
        {Array.isArray(r.cleanup_buckets) && r.cleanup_buckets.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-cargo-text font-semibold text-[15px]">
              Detailed Cleanup Recommendations by Priority
            </h3>
            {r.cleanup_buckets.map((bucket: any) => (
              <PriorityBucket key={bucket.priority} bucket={bucket} />
            ))}
          </div>
        )}
        {r.final_summary && (
          <FinalSummaryCallout summary={r.final_summary} />
        )}
      </div>
    );
  }

  return null;
};

export default MessageBubble;
